import {calculateErrorMessage} from 'common/helpers'
import {Location, NavigateFunction} from 'react-router'

import {observeForSelector} from './walkthroughDom'
import {ComponentRouteInfo, isOnCorrectRoute} from './walkthroughRouteRegistry'

function normalizePath(path: string): string {
  try {
    if (!path) return ''
    const withoutHash = String(path).split('#')[0]
    let decoded = decodeURIComponent(withoutHash)
    if (decoded.length > 1 && decoded.endsWith('/')) decoded = decoded.slice(0, -1)
    return decoded
  } catch {
    return String(path)
  }
}

export interface NavigationResult {
  success: boolean
  message: string
  timedOut?: boolean
  error?: string
  messageId?: string
  messageParams?: Record<string, any>
  telemetry?: NavigationTelemetry
}

export interface NavigationTelemetry {
  timeToMount: number
  timeToDataLoad: number
  validatorFailures: string[]
  didTimeout: boolean
  totalDuration: number
}

interface NavigationConfig {
  routeChangeTimeout?: number
  mountTimeout?: number
  dataLoadTimeout?: number
  pollInterval?: number
  verbose?: boolean
}

const DEFAULT_CONFIG: Required<NavigationConfig> = {
  routeChangeTimeout: 5000,
  mountTimeout: 8000,
  dataLoadTimeout: 12000,
  pollInterval: 200,
  verbose: true,
}

export class SmartRouteNavigator {
  private navigate: NavigateFunction
  private location: Location
  private config: Required<NavigationConfig>

  constructor(navigate: NavigateFunction, location: Location, config?: NavigationConfig) {
    this.navigate = navigate
    this.location = location
    this.config = {...DEFAULT_CONFIG, ...config}
  }

  async navigateToComponent(componentInfo: ComponentRouteInfo): Promise<NavigationResult> {
    const startTime = Date.now()
    const telemetry: NavigationTelemetry = {
      timeToMount: 0,
      timeToDataLoad: 0,
      validatorFailures: [],
      didTimeout: false,
      totalDuration: 0,
    }

    try {
      if (typeof this.navigate !== 'function') {
        return {
          success: false,
          message: 'Navigate function is not available',
          messageId: 'WALKTHROUGH.NAV.NAVIGATE_NOT_AVAILABLE',
          error: 'Navigation cannot proceed without valid navigate function',
          messageParams: {},
          telemetry,
        }
      }

      const alreadyOnRoute = isOnCorrectRoute(componentInfo)

      if (!alreadyOnRoute) {
        try {
          this.navigate(componentInfo.route, {replace: false})
        } catch (error) {
          telemetry.totalDuration = Date.now() - startTime
          return {
            success: false,
            message: 'Navigation failed',
            messageId: 'WALKTHROUGH_PROVIDER.ERROR.NAVIGATION_FAILED',
            error: error instanceof Error ? error.message : String(error),
            messageParams: {},
            telemetry,
          }
        }

        const routeChangeResult = await this.waitForRouteChange(
          componentInfo.route,
          this.config.routeChangeTimeout
        )

        if (!routeChangeResult.success) {
          telemetry.totalDuration = Date.now() - startTime
          telemetry.didTimeout = !!routeChangeResult.timedOut
          return {...routeChangeResult, telemetry}
        }
      }

      const mountStartTime = Date.now()

      const mountResult = await this.waitForComponentMountEnhanced(
        componentInfo,
        componentInfo.mountTimeout || this.config.mountTimeout
      )

      telemetry.timeToMount = Date.now() - mountStartTime

      if (!mountResult.success) {
        telemetry.totalDuration = Date.now() - startTime
        telemetry.didTimeout = !!mountResult.timedOut
        telemetry.validatorFailures = mountResult.telemetry?.validatorFailures || []
        return {...mountResult, telemetry}
      }

      const dataLoadStartTime = Date.now()

      if (componentInfo.dataLoadingIndicators && componentInfo.dataLoadingIndicators.length > 0) {
        const dataLoadResult = await this.waitForDataLoad(
          componentInfo,
          componentInfo.dataLoadTimeout || this.config.dataLoadTimeout
        )

        telemetry.timeToDataLoad = Date.now() - dataLoadStartTime

        if (!dataLoadResult.success) {
          telemetry.totalDuration = Date.now() - startTime
          telemetry.didTimeout = !!dataLoadResult.timedOut
          return {...dataLoadResult, telemetry}
        }
      }
      await this.delay(500)

      telemetry.totalDuration = Date.now() - startTime

      return {
        success: true,
        message: `Successfully navigated to ${componentInfo.componentName} and component is ready`,
        messageId: 'WALKTHROUGH.NAV.SUCCESS_NAVIGATED',
        messageParams: {componentName: componentInfo.componentName},
        telemetry,
      }
    } catch (error) {
      telemetry.totalDuration = Date.now() - startTime
      return {
        success: false,
        message: 'Navigation failed with unexpected error',
        messageId: 'WALKTHROUGH.NAV.NAVIGATION_UNEXPECTED_ERROR',
        error: error instanceof Error ? error.message : String(error),
        messageParams: {},
        telemetry,
      }
    }
  }

  private async waitForRouteChange(
    targetRoute: string,
    timeout: number
  ): Promise<NavigationResult> {
    return new Promise((resolve) => {
      const startTime = Date.now()
      const normalizedTarget = normalizePath(targetRoute)
      let resolvedOrTimedOut = false

      const cleanup = () => {
        window.removeEventListener('popstate', onPop)
        resolvedOrTimedOut = true
      }

      const onPop = () => {
        try {
          checkRoute()
        } catch (e) {
          calculateErrorMessage(e)
        }
      }

      const checkRoute = () => {
        const currentPath = normalizePath(window.location.pathname)

        if (currentPath === normalizedTarget) {
          if (!resolvedOrTimedOut) {
            cleanup()
            resolve({
              success: true,
              message: `Route changed to ${targetRoute}`,
              messageId: 'WALKTHROUGH.NAV.ROUTE_CHANGED',
              messageParams: {route: targetRoute},
            })
          }
          return
        }

        if (Date.now() - startTime > timeout) {
          if (!resolvedOrTimedOut) {
            cleanup()
            resolve({
              success: false,
              message: `Route change timeout: Expected ${targetRoute}, still on ${currentPath}`,
              messageId: 'WALKTHROUGH.NAV.ROUTE_CHANGE_TIMEOUT',
              messageParams: {expected: targetRoute, current: currentPath},
              timedOut: true,
            })
          }
          return
        }

        if (!resolvedOrTimedOut) setTimeout(checkRoute, this.config.pollInterval)
      }

      window.addEventListener('popstate', onPop)
      checkRoute()
    })
  }

  private async waitForComponentMountEnhanced(
    componentInfo: ComponentRouteInfo,
    timeout: number
  ): Promise<NavigationResult> {
    const startTime = Date.now()
    const validatorFailures: string[] = []

    const rootSelectors = componentInfo.componentRootSelector.split(',').map((s) => s.trim())
    let rootExists = false

    for (const selector of rootSelectors) {
      if (this.checkElementExists(selector)) {
        rootExists = true
        break
      }
    }

    if (!rootExists) {
      for (const selector of rootSelectors) {
        const observerResult = await observeForSelector(selector, {
          root: document.body,
          timeout: componentInfo.observerTimeout || 10000,
          verbose: this.config.verbose,
        })

        if (observerResult.success && observerResult.element) {
          rootExists = true
          break
        }
      }

      if (!rootExists) {
        return {
          success: false,
          message: `Component mount timeout: Root selectors not found after ${Date.now() - startTime}ms`,
          messageId: 'WALKTHROUGH.NAV.COMPONENT_MOUNT_ROOT_TIMEOUT',
          messageParams: {ms: Date.now() - startTime},
          timedOut: true,
          telemetry: {
            timeToMount: Date.now() - startTime,
            timeToDataLoad: 0,
            validatorFailures: rootSelectors,
            didTimeout: true,
            totalDuration: Date.now() - startTime,
          },
        }
      }
    }

    const presentValidators = componentInfo.readinessValidators.filter((selector) =>
      this.checkElementExists(selector)
    )

    if (presentValidators.length > 0) {
      return {
        success: true,
        message: `Component ${componentInfo.componentName} mounted successfully`,
        messageId: 'WALKTHROUGH.NAV.COMPONENT_MOUNT_SUCCESS',
        messageParams: {componentName: componentInfo.componentName},
        telemetry: {
          timeToMount: Date.now() - startTime,
          timeToDataLoad: 0,
          validatorFailures: [],
          didTimeout: false,
          totalDuration: Date.now() - startTime,
        },
      }
    }

    for (const validator of componentInfo.readinessValidators) {
      const observerResult = await observeForSelector(validator, {
        root: document.body,
        timeout: Math.min(
          componentInfo.observerTimeout || 8000,
          timeout - (Date.now() - startTime)
        ),
        verbose: this.config.verbose,
      })

      if (observerResult.success && observerResult.element) {
        return {
          success: true,
          message: `Component ${componentInfo.componentName} mounted successfully (via observer)`,
          messageId: 'WALKTHROUGH.NAV.COMPONENT_MOUNT_SUCCESS_OBSERVER',
          messageParams: {componentName: componentInfo.componentName},
          telemetry: {
            timeToMount: Date.now() - startTime,
            timeToDataLoad: 0,
            validatorFailures: [],
            didTimeout: false,
            totalDuration: Date.now() - startTime,
          },
        }
      } else {
        validatorFailures.push(validator)
      }
    }

    return {
      success: false,
      message: `Component mount timeout: No readiness validators found after ${Date.now() - startTime}ms`,
      messageId: 'WALKTHROUGH.NAV.COMPONENT_MOUNT_NO_VALIDATORS_TIMEOUT',
      messageParams: {ms: Date.now() - startTime},
      timedOut: true,
      telemetry: {
        timeToMount: Date.now() - startTime,
        timeToDataLoad: 0,
        validatorFailures,
        didTimeout: true,
        totalDuration: Date.now() - startTime,
      },
    }
  }

  private async waitForComponentMount(
    componentInfo: ComponentRouteInfo,
    timeout: number
  ): Promise<NavigationResult> {
    return new Promise((resolve) => {
      const startTime = Date.now()

      const checkMount = () => {
        const rootSelectors = componentInfo.componentRootSelector.split(',').map((s) => s.trim())
        let rootExists = false

        for (const selector of rootSelectors) {
          if (this.checkElementExists(selector)) {
            rootExists = true
            break
          }
        }

        if (!rootExists) {
          if (Date.now() - startTime > timeout) {
            resolve({
              success: false,
              message: `Component mount timeout: Root selectors not found after ${timeout}ms`,
              messageId: 'WALKTHROUGH.NAV.COMPONENT_MOUNT_ROOT_TIMEOUT',
              messageParams: {ms: timeout},
              timedOut: true,
            })
            return
          }

          setTimeout(checkMount, this.config.pollInterval)
          return
        }

        const presentValidators = componentInfo.readinessValidators.filter((selector) =>
          this.checkElementExists(selector)
        )

        if (presentValidators.length > 0) {
          resolve({
            success: true,
            message: `Component ${componentInfo.componentName} mounted successfully`,
            messageId: 'WALKTHROUGH.NAV.COMPONENT_MOUNT_SUCCESS',
            messageParams: {componentName: componentInfo.componentName},
          })
          return
        }

        if (Date.now() - startTime > timeout) {
          componentInfo.readinessValidators.filter((selector) => !this.checkElementExists(selector))
          resolve({
            success: false,
            message: `Component mount timeout: No readiness validators found after ${timeout}ms`,
            messageId: 'WALKTHROUGH.NAV.COMPONENT_MOUNT_NO_VALIDATORS_TIMEOUT',
            messageParams: {ms: timeout},
            timedOut: true,
          })
          return
        }

        setTimeout(checkMount, this.config.pollInterval)
      }

      checkMount()
    })
  }

  private async waitForDataLoad(
    componentInfo: ComponentRouteInfo,
    timeout: number
  ): Promise<NavigationResult> {
    return new Promise((resolve) => {
      const startTime = Date.now()

      const checkDataLoad = () => {
        let loadingIndicatorsPresent = false

        for (const selector of componentInfo.dataLoadingIndicators!) {
          const elements = document.querySelectorAll(selector)
          for (let i = 0; i < elements.length; i++) {
            const el = elements[i] as HTMLElement
            if (this.isElementVisible(el)) {
              loadingIndicatorsPresent = true
              break
            }
          }
          if (loadingIndicatorsPresent) break
        }

        if (!loadingIndicatorsPresent) {
          resolve({
            success: true,
            message: `Data loaded for ${componentInfo.componentName}`,
            messageId: 'WALKTHROUGH.NAV.DATA_LOADED',
            messageParams: {componentName: componentInfo.componentName},
          })
          return
        }

        if (Date.now() - startTime > timeout) {
          resolve({
            success: true,
            message: `Data load timeout: Proceeding anyway`,
            messageId: 'WALKTHROUGH.NAV.DATA_LOAD_TIMEOUT',
            messageParams: {},
          })
          return
        }
        setTimeout(checkDataLoad, this.config.pollInterval)
      }
      checkDataLoad()
    })
  }

  private checkElementExists(selector: string): boolean {
    try {
      return document.querySelector(selector) !== null
    } catch (error) {
      calculateErrorMessage(error)
      return false
    }
  }

  private isElementVisible(element: HTMLElement): boolean {
    if (!element) return false

    const style = window.getComputedStyle(element)

    if (style.display === 'none') return false
    if (style.visibility === 'hidden') return false
    if (style.opacity === '0') return false

    const rect = element.getBoundingClientRect()
    return rect.width > 0 && rect.height > 0
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }

  updateLocation(location: Location): void {
    this.location = location
  }
}

export function createSmartNavigator(
  navigate: NavigateFunction,
  location: Location,
  config?: NavigationConfig
): SmartRouteNavigator {
  return new SmartRouteNavigator(navigate, location, config)
}
